// libraries
import { createSelector } from 'reselect';
import { isEqual } from 'lodash';
import type { PaymentProduct, StateTree } from '@makemydeal/dr-dash-types';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { ApplicationName } from '@makemydeal/dr-shared-types';

// consts/enums
import { ACQUISITION_FEE_ID, DEALER_ROLE, SHOPPER_ROLE } from '../constants';

// selectors
import { isDealerUser, getUserRole } from './widgetSettingsSelectors';
import {
    getNetCashDown,
    getCapReductionRebate,
    getCapReductionTradeIn,
    getCapCostReductionTax,
    getItemizedLenderFees,
    getTotalTax,
    filterCountedLenderFees
} from './offerRedux';
import {
    getDealerFeeTotal,
    getDmvFeeTotal,
    getMonthlyPayment,
    getCappedDealerFeesTotal,
    getIncludedCappedLenderFees,
    getApplicationName,
    getSellingPrice
} from './offerSelectors';
import { getIsVersionSpecificLoad } from './offerInfoSelectors';
import {
    getAccessoriesPaymentProducts,
    getAccessoriesTotalAmount,
    getCapAccessoriesTotal
} from '../selectors/accessoriesSelectors';
import * as offerReduxSelectors from './offerRedux';
import { getBridgeId } from './bridgeUserSelectors';
import { getHasVehicle } from './vehicleSelectors';
import * as vehicleProtectionSelectors from './vehicleProtectionSelectors';
import { getVppPaymentProducts } from './vehicleProtectionSelectors';
import { wasTheLastFailure } from './deskingSelectors';
import * as tradeSelectors from './trade';
import { getIsError } from './payment';
import type { IDealerFee, OfferType } from '@makemydeal/dr-platform-types';
import { CASH } from '@makemydeal/dr-platform-types';
import { getProposalPaymentRangeConfig } from './proposalSelectors';

export const getPaymentProducts = (state: StateTree): PaymentProduct[] => {
    const accessories = getAccessoriesPaymentProducts(state);
    const products = getVppPaymentProducts(state);

    return [...accessories, ...products];
};

export const isDeskingUser = (state: StateTree): boolean => {
    const isDealer = isDealerUser(state);
    const isDeskingManager = getUserRole(state) === 'desking_manager';
    return isDealer && isDeskingManager;
};

export const getPersona = (state: StateTree): string => {
    return isDealerUser(state) ? DEALER_ROLE : SHOPPER_ROLE;
};

/**
 * Returns the total addon for finance and cash.
 * Total Add-ons = Accessories, Protection Products, Fees (Dealer & Govt), plus Lender fees once
 * either lender-fee toggle is on. US2116905_Enable_Lender_Fees_From_Priced_Term takes the lender
 * component from the priced term; with only DE769095_Include_Lender_Fees_In_Add_Ons on it is the
 * client-side sum of the itemized rows, which is what DE769095 shipped.
 */
export const getTotalAddons = (state: StateTree): number => {
    const protectionTotal = vehicleProtectionSelectors.getVppSummary(state).totalProductPrice || 0;
    // TODO: When accessories is implemented, make sure the total includes only capitalized accessories for lease
    const accessoriesTotal = getAccessoriesTotalAmount(state);
    const govFeesTotal = getDmvFeeTotal(state);
    const dealerFeeTotal = getDealerFeeTotal(state);
    const lenderFeeTotal = getCountedTotalLenderFees(state);

    return govFeesTotal + accessoriesTotal + protectionTotal + dealerFeeTotal + lenderFeeTotal;
};

export type DealerFee = {
    dealerFeeTypeCode: number;
    dealerFeeAmount: number;
    dealerFeeName: string;
    dealerFeeDesc?: string;
    dealerFeeCapped?: boolean;
    originalCategory?: string;
};

export const getDealerFees = (state: StateTree, capped: boolean): DealerFee[] => {
    const dealerFees = offerReduxSelectors.getDealerFees(state) as DealerFee[];
    const filteredFees = dealerFees.filter(
        (fee) =>
            fee.originalCategory?.toLowerCase() === 'dealer' && // must be dealer (not lender)
            fee.dealerFeeCapped === capped && // capped or not
            fee.dealerFeeTypeCode !== ACQUISITION_FEE_ID
    ); // exclude lender fee: acq
    return filteredFees;
};

export const getDealerFeesTotal = (state: StateTree, capped: boolean): number => {
    const fees = getDealerFees(state, capped);
    const result = fees.reduce((accumulator, cur) => accumulator + cur.dealerFeeAmount, 0);
    return result;
};

export const getDealerFeesUpfrontTotal = (state: StateTree): number => {
    const fees = getDealerFees(state, false);
    const result = fees.reduce((accumulator, cur) => accumulator + cur.dealerFeeAmount, 0);
    return result;
};

export const getDealerFeesUpfront = (state: StateTree) => {
    const fees = getDealerFees(state, false);
    return fees;
};

/**
 * Get goverment fees for current offer type and term.
 * @param state
 * @param capped Capitalized or not
 */
export const getGovernmentFees = (state: StateTree, capped: boolean) => {
    const govFees = offerReduxSelectors.getGovernmentFees(state);
    return govFees.fee.taxFee.filter((t) => t.capped === capped);
};
export const getGovernmentFeesUpFront = (state: StateTree) => {
    return getGovernmentFees(state, false);
};

export const getIncludedCapLenderFeesTotal = (state: StateTree) => {
    const lenderFees = getIncludedCappedLenderFees(state);
    return lenderFees.reduce((accumulator, cur) => accumulator + cur.dealerFeeAmount, 0);
};

const isFeeIncludedInCalc = (fee: IDealerFee) => Boolean(fee.includedInCalc || fee.isFeeIncludedInCalculation);

/**
 * Both lender-fee toggles need the itemized rows filtered to what counts in the calculation:
 * DE769095 introduced that filter and US2116905 relies on it to keep the rows reconcilable with the
 * priced total.
 */
const isAnyLenderFeeToggleEnabled = (state: StateTree): boolean =>
    featureToggleSelectors.isLenderFeesInAddOnsEnabled(state) || featureToggleSelectors.isLenderFeesFromPricedTermEnabled(state);

/**
 * The lender fees that getLenderFeesTotal sums, and the rows the Deal Summary itemizes beneath that
 * total, so both must read from this selector or the total stops matching its own rows. Total
 * Add-ons is no longer one of those totals once US2116905_Enable_Lender_Fees_From_Priced_Term is
 * on: it reads the priced term figure, so it agrees with these rows by assumption rather than by
 * construction.
 * Memoized because the filtered branch allocates: Addons reads it through useSelector, which
 * compares by reference and would re-render on every dispatch given a fresh array each call.
 */
export const getCountedLenderFees = createSelector(
    [(state: StateTree) => getItemizedLenderFees(state), (state: StateTree) => isAnyLenderFeeToggleEnabled(state)],
    (itemized: IDealerFee[], isEnabled: boolean): IDealerFee[] => filterCountedLenderFees(itemized, isEnabled),
    {
        memoizeOptions: {
            resultEqualityCheck: isEqual
        }
    }
);

export const getLenderFeesTotal = (state: StateTree) =>
    getCountedLenderFees(state).reduce((accumulator, cur) => accumulator + cur.dealerFeeAmount, 0);

/**
 * Total Add-ons counts lender fees under either toggle, but from a different source each way:
 * US2116905 reads the figure Payment Services priced, DE769095 alone keeps the client-side sum of
 * the itemized rows that it originally shipped.
 */
const getCountedTotalLenderFees = (state: StateTree): number => {
    if (featureToggleSelectors.isLenderFeesFromPricedTermEnabled(state)) {
        return offerReduxSelectors.getTotalLenderFees(state);
    }
    return featureToggleSelectors.isLenderFeesInAddOnsEnabled(state) ? getLenderFeesTotal(state) : 0;
};

const getCountedCapitalizedLenderFees = (state: StateTree): number =>
    featureToggleSelectors.isLenderFeesFromPricedTermEnabled(state) ? offerReduxSelectors.getCapitalizedLenderFees(state) : 0;

export const getLenderFeesUpFront = (state: StateTree) => {
    const lenderFees = getItemizedLenderFees(state);
    return lenderFees.filter((t) => t.dealerFeeCapped === false && t.dealerFeeAmount > 0 && isFeeIncludedInCalc(t));
};

//TODO: remove this after the lender fees mapping is validated
export const getLenderFeesUpFrontTotal = (state: StateTree) => {
    const total = getLenderFeesUpFront(state).reduce((acc, cur) => acc + cur.dealerFeeAmount, 0);
    return total;
};

/**
 * Returns the Net Selling Price for Finance/Cash
 * Net Selling Price = Selling Price + Total Add-ons + Taxes

 */
export const getNetSellingPrice = (state: StateTree): number => {
    const sellingPrice = getSellingPrice(state);
    const totalAddons = getTotalAddons(state);
    const totalTax = getTotalTax(state);

    return sellingPrice + totalTax + totalAddons;
};

export const getCapCostReduction = (state: StateTree) => {
    const netCashDown = getNetCashDown(state);
    const reductionRebate = getCapReductionRebate(state);
    const reductionTradeIn = getCapReductionTradeIn(state);
    return netCashDown + reductionRebate + reductionTradeIn;
};

/*
 The sum of:
 Protection Products, Capped Accessories, Capped Fees (gov & dealer), First Month's payment,
 plus Capitalized Lender Fees once US2116905_Enable_Lender_Fees_From_Priced_Term is on
 */
export const getAddsToCapCostTotal = (state: StateTree) => {
    const protectionTotal = vehicleProtectionSelectors.getCapVppTotal(state);
    const capDealerFeeTotal = getCappedDealerFeesTotal(state);
    const accessoriesTotal = getCapAccessoriesTotal(state);
    const governmentFeeTotal = offerReduxSelectors.getGovernmentFees(state).cappedFeeTotal;
    const capLenderFeeTotal = getCountedCapitalizedLenderFees(state);

    return accessoriesTotal + capDealerFeeTotal + governmentFeeTotal + protectionTotal + capLenderFeeTotal;
};

export function getTotalUpfronts(state: StateTree): number {
    const ccrTax = getCapCostReductionTax(state);
    const firstPayment = getMonthlyPayment(state);

    return ccrTax + firstPayment;
}

export function getCapitalizedTax(state: StateTree): number {
    const isTaxCapped = offerReduxSelectors.isTaxCapped(state);
    const advanceFlatTax = offerReduxSelectors.getAdvanceFlatTax(state);
    const advanceSalesTax = offerReduxSelectors.getAdvanceSalesTax(state);
    const hasManualTotalTax = offerReduxSelectors.hasManualTotalTax(state);

    return !isTaxCapped ? 0 : hasManualTotalTax ? advanceFlatTax : advanceSalesTax;
}

export function getUpfrontAdvanceFlatTax(state: StateTree): number {
    const hasManualTotalTax = offerReduxSelectors.hasManualTotalTax(state);
    const upfrontTaxTotal = offerReduxSelectors.getUpfrontTaxTotal(state);

    return hasManualTotalTax ? 0 : upfrontTaxTotal;
}

export const getLastFailureActionIfError = (actionTypeToCheck: string) => (state: StateTree) => {
    const lastFailure = wasTheLastFailure(actionTypeToCheck)(state);
    const isPaymentError = getIsError(state);
    return isPaymentError && lastFailure;
};

export const taxesAndFeesTotal = (state: StateTree): number => {
    const currentOfferType = offerReduxSelectors.getCurrentOfferType(state);
    return offerReduxSelectors.getTaxesAndFeesTotal(state, currentOfferType);
};

export const getPaymentRangeByOfferType = (state: StateTree, offerType: OfferType): number => {
    const isPaymentRangeEnabled = dealerSelectors.isShowPaymentRangeEnabled(state);
    const paymentRange = dealerSelectors.getDealerPaymentRangeByOfferType(state, offerType);
    if (isPaymentRangeEnabled && paymentRange > 0) {
        return paymentRange;
    }
    return 0;
};

export const getMvPaymentRangeByOfferType = (state: StateTree, offerType: OfferType): number => {
    if (offerType === CASH) {
        return 0;
    }
    const config = getProposalPaymentRangeConfig(state);
    if (!config?.active) {
        return 0;
    }
    try {
        const parsed = config.value ? JSON.parse(config.value) : undefined;
        if (!parsed) {
            return 0;
        }
        const spread = offerType === 'lease' ? parsed.lease : parsed.finance;
        return typeof spread === 'number' && spread > 0 ? spread : 0;
    } catch {
        return 0;
    }
};

export const getTotalAmountDue = (state: StateTree): number => {
    const dueAtSigningPaidByFromDueInAdvance = offerReduxSelectors.getPaidAtSigning(state);
    const dueAtSigningFromDueInAdvance = offerReduxSelectors.getDiaDueAtSigning(state);
    return dueAtSigningPaidByFromDueInAdvance || dueAtSigningFromDueInAdvance;
};

export const getTotalTradeInTaxReduction = (state: StateTree) => {
    const totalTradeTaxReduction = tradeSelectors.getTotalTradeInTaxReduction(state);
    const taxReduction = offerReduxSelectors.getTotalTaxReduction(state);
    // `||`, not `??`: a recalculated term carries totalTaxReduction: 0 while its taxes are unchanged, so
    // the zero means "not reported" rather than "no reduction" (DE778451). ShopperPlatform's twin
    // selector and tradesPayloadUtil both use `||` for this same figure.
    return taxReduction || totalTradeTaxReduction;
};

export const getIsNonMvOfferComment = (state: StateTree): boolean => {
    const applicationName = getApplicationName(state);
    const isNonMvSource = !!applicationName && applicationName.toUpperCase() !== ApplicationName.CMD;
    return isNonMvSource && !!offerReduxSelectors.getMessage(state);
};

export const getIsOfferCommentBannerEligible = (state: StateTree): boolean =>
    !getIsVersionSpecificLoad(state) && getIsNonMvOfferComment(state);

export const getIsTraditionalOrIntakeWithoutVehicle = (state: StateTree): boolean => {
    if (!featureToggleSelectors.isLoosenedCtaValidationEnabled(state)) return false;
    const isTraditionalMode = dealerSelectors.getIsTraditionalMode(state);
    const isSalesIntakeMode = dealerSelectors.isSalesIntakeMode(state);
    return Boolean(isTraditionalMode || isSalesIntakeMode) && !getHasVehicle(state);
};

export const getAssistantSessionKey = (state: StateTree): string =>
    `${offerReduxSelectors.getDealXgId(state)}|${dealerSelectors.getDealerId(state)}|${getBridgeId(state)}`;
